import { useCallback } from 'react';

export interface IntentAnalysisResult {
    isSummary: boolean;
    petName?: string;
    confidence: number;
    intent: string;
    entities: any;
}

/**
 * Custom hook for semantic intent classification
 * 
 * Handles:
 * - Intent classification using semantic similarity
 * - Summary request detection
 * - Pet name extraction from message
 * - Fallback to keyword-based detection
 * 
 * @returns analyzeSummaryRequest function
 */
export const useSemanticIntent = () => {
    /**
     * Analyze user message to detect intent (summary, roadmap, etc.)
     * @param message - User message to analyze
     * @param language - Detected language of the message
     * @returns IntentAnalysisResult with intent, confidence, and entities
     */
    const analyzeSummaryRequest = useCallback(async (message: string, language: string): Promise<IntentAnalysisResult> => {
        try {
            console.log('🔍 useSemanticIntent: Starting intent analysis for:', message.substring(0, 50) + '...');

            // Import the semantic intent classification service
            const { semanticIntentClassificationService } = await import('../services/semanticIntentClassificationService');

            // Classify the intent using semantic similarity
            const intentResult = await semanticIntentClassificationService.classifyIntent(message);

            const isSummary = semanticIntentClassificationService.isSummaryRequest(intentResult);
            const petName = intentResult.entities.petName;
            const confidence = intentResult.confidence;

            console.log('🎯 useSemanticIntent: Analysis result:', {
                message: message.substring(0, 50) + '...',
                detectedLanguage: intentResult.language,
                intent: intentResult.intent,
                confidence: confidence,
                isSummary: isSummary,
                petName: petName,
                explanation: intentResult.explanation
            });

            return {
                isSummary,
                petName,
                confidence,
                intent: intentResult.intent,
                entities: intentResult.entities
            };
        } catch (error) {
            console.warn('⚠️ useSemanticIntent: Analysis error, falling back to keyword detection:', error);

            // Fallback to basic keyword detection if semantic analysis fails
            const hasSummaryKeywords = message.includes('总结') || message.includes('summary') ||
                message.includes('最近') || message.includes('recent') ||
                message.includes('话题') || message.includes('topics') ||
                message.includes('resumen') || message.includes('résumé') ||
                message.includes('recentes') || message.includes('récentes') ||
                message.includes('temas') || message.includes('sujets');

            return {
                isSummary: hasSummaryKeywords,
                petName: undefined,
                confidence: hasSummaryKeywords ? 0.5 : 0,
                intent: hasSummaryKeywords ? 'retrieve_chat_history' : 'general_question',
                entities: {}
            };
        }
    }, []);

    return {
        analyzeSummaryRequest
    };
};
